import { apiClient } from '../const';

const rawJson = { skipBodyWrap: true };

/** @typedef {{ manual_buy: boolean, sell: boolean, autopay: boolean }} GoldTradingScopes */

export const SCOPE_KEYS = ['manual_buy', 'sell', 'autopay'];

export const SCOPE_OPTIONS = [
  {
    key: 'manual_buy',
    label: 'Manual buying',
    chip: 'Manual buying blocked',
  },
  {
    key: 'sell',
    label: 'Sell / withdraw',
    chip: 'Sell / withdraw blocked',
  },
  {
    key: 'autopay',
    label: 'Autopay — investment rules, SIP & Sanduk',
    chip: 'Autopay blocked (rules, SIP & Sanduk)',
    help: 'Pauses Autopay execution only. Creating rules / SIP / Sanduk may still be allowed.',
  },
];

/** @returns {GoldTradingScopes} */
export const emptyScopes = () => ({
  manual_buy: false,
  sell: false,
  autopay: false,
});

/** @returns {GoldTradingScopes} */
export const allScopes = () => ({
  manual_buy: true,
  sell: true,
  autopay: true,
});

/**
 * @param {Partial<GoldTradingScopes>|null|undefined} scopes
 * @returns {GoldTradingScopes}
 */
export const normalizeScopes = (scopes) => ({
  manual_buy: Boolean(scopes?.manual_buy),
  sell: Boolean(scopes?.sell),
  autopay: Boolean(scopes?.autopay),
});

/**
 * @param {GoldTradingScopes} scopes
 * @returns {boolean}
 */
export const hasAnyScope = (scopes) =>
  Boolean(scopes?.manual_buy || scopes?.sell || scopes?.autopay);

/**
 * @param {GoldTradingScopes} scopes
 * @returns {boolean}
 */
export const hasAllScopes = (scopes) =>
  Boolean(scopes?.manual_buy && scopes?.sell && scopes?.autopay);

/**
 * Map API errors to user-visible messages.
 * @param {unknown} err
 * @returns {string}
 */
export function mapTradingApiError(err) {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const code = data?.code || data?.error_code;
  const message = data?.message || err?.message;

  if (code === 'SCOPES_REQUIRED') {
    return message || 'Select at least one channel to disable.';
  }
  if (code === 'INVALID_DISABLED_UNTIL') {
    return message || 'Disabled-until datetime is invalid.';
  }
  if (status === 403) {
    return message || 'You do not have permission for this action.';
  }
  if (status === 401) {
    return message || 'Session expired. Please sign in again.';
  }
  if (status === 400) {
    return message || 'Invalid request. Please check your inputs.';
  }
  return message || 'Something went wrong. Please try again.';
}

/**
 * GET /admin/system/gold-trading
 * Permission: trading.killswitch.read
 */
export const getGoldTradingStatus = async () => {
  const res = await apiClient.get('/admin/system/gold-trading');
  return res.data;
};

/**
 * POST /admin/system/gold-trading/disable
 * Permission: trading.killswitch.manage
 * @param {{ reason?: string, until?: string, scopes: GoldTradingScopes }} payload
 */
export const disableGoldTrading = async (payload) => {
  const body = {
    scopes: normalizeScopes(payload?.scopes),
  };
  if (payload?.reason) body.reason = payload.reason;
  if (payload?.until) body.until = payload.until;
  const res = await apiClient.post('/admin/system/gold-trading/disable', body, rawJson);
  return res.data;
};

/**
 * POST /admin/system/gold-trading/enable
 * Permission: trading.killswitch.manage
 */
export const enableGoldTrading = async () => {
  const res = await apiClient.post('/admin/system/gold-trading/enable', {}, rawJson);
  return res.data;
};

/**
 * GET /admin/system/safegold/cash-balance
 * Returns partner SafeGold wallet / cash float balance.
 */
export const getSafegoldCashBalance = async () => {
  const res = await apiClient.get('/admin/system/safegold/cash-balance');
  return res.data;
};

const BALANCE_KEYS = [
  'cash_balance',
  'cashBalance',
  'cash_balance_inr',
  'cashBalanceInr',
  'wallet_balance',
  'walletBalance',
  'wallet_balance_inr',
  'available_balance',
  'availableBalance',
  'available_cash',
  'availableCash',
  'available_cash_inr',
  'safegold_cash_balance',
  'sg_cash_balance',
  'float_balance',
  'floatBalance',
  'balance_inr',
  'balanceInr',
  'balance',
  'amount_inr',
  'amountInr',
  'amount',
  'value',
];

/**
 * @param {unknown} value
 * @returns {number|null}
 */
const toFiniteNumber = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'object') return null;
  const n = Number(String(value).replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : null;
};

/**
 * Normalize cash-balance API payloads into a single INR number (or null).
 * Supports shapes like:
 *   { success, data: { cash_balance } }
 *   { data: 12345 }
 *   { cash_balance: "12,345.50" }
 * @param {unknown} payload
 * @returns {number|null}
 */
export const parseSafegoldCashBalance = (payload) => {
  if (payload == null) return null;

  const direct = toFiniteNumber(payload);
  if (direct != null) return direct;

  const queue = [payload];
  const seen = new Set();

  while (queue.length) {
    const node = queue.shift();
    if (!node || typeof node !== 'object' || seen.has(node)) continue;
    seen.add(node);

    // Prefer known keys at this level
    for (const key of BALANCE_KEYS) {
      if (Object.prototype.hasOwnProperty.call(node, key)) {
        const n = toFiniteNumber(node[key]);
        if (n != null) return n;
      }
    }

    // data / result may itself be the number
    for (const wrapKey of ['data', 'result', 'payload']) {
      if (Object.prototype.hasOwnProperty.call(node, wrapKey)) {
        const wrapped = toFiniteNumber(node[wrapKey]);
        if (wrapped != null) return wrapped;
        if (node[wrapKey] && typeof node[wrapKey] === 'object') {
          queue.push(node[wrapKey]);
        }
      }
    }

    // Fall through nested objects (shallow BFS)
    for (const value of Object.values(node)) {
      if (value && typeof value === 'object') queue.push(value);
    }
  }

  return null;
};
